import { BRAND_URL, WithHalfdot } from "@elyxeer/ui";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { DeleteAccountDrawer } from "~/components/account/DeleteAccountDrawer";
import { TeamChatDialog } from "~/components/chat/TeamChatDialog";
import type { SettingsAction } from "~/components/settings/settings-nav";
import {
  LEGAL_SECTION_ITEM,
  SETTINGS_ITEMS,
} from "~/components/settings/settings-nav";
import { SettingsRow } from "~/components/settings/SettingsRow";
import { Screen } from "~/components/ui/Screen";
import { useLocale } from "~/i18n";
import { WELCOME_PATH } from "~/lib/onboarding";
import { SAVED_PATH } from "~/routes/SavedRoute";
import { useAuthStore } from "~/store/auth-store";

/**
 * Profile — a menu, not a form.
 *
 * Each setting is a row that navigates to its own screen with its own path, so a
 * setting can be linked to directly and each screen holds the controls for exactly one
 * decision. Rows stay label-only; values and controls belong on those detail screens.
 * Membership is the one external action because subscription management lives in the
 * member's storefront account rather than inside this app.
 */
export function ProfileRoute() {
  const { t } = useTranslation();
  const { formatMonthYear } = useLocale();

  const user = useAuthStore((state) => state.user);
  const signOut = useAuthStore((state) => state.signOut);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const chatOpen = searchParams.get("chat") === "open";

  if (!user) return null;

  const membershipStatusKey =
    user.membership_status === "active"
      ? "membership.status.activeTitle"
      : "membership.status.unknownTitle";
  // A physical subscription is the stronger status. The app-only gift gets its own
  // soft upsell only while there is no active physical membership to report.
  const hasGiftedAppAccess =
    user.has_membership_override && user.membership_status !== "active";

  const handleSettingsAction = (action: SettingsAction) => {
    if (action === "delete-account") setConfirmingDelete(true);
  };

  const setChatOpen = (open: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (open) next.set("chat", "open");
    else next.delete("chat");
    setSearchParams(next, { replace: true });
  };

  return (
    <Screen maxWidth="lg">
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, md: 4 } }}
      >
        <Box>
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            <WithHalfdot size={11}>{t("profile.title")}</WithHalfdot>
          </Typography>
          <Typography
            variant="T3"
            sx={{ color: "text.muted", display: "block", marginTop: 1 }}
          >
            {t("profile.memberSince", {
              date: formatMonthYear(user.created_at),
            })}
          </Typography>
        </Box>

        {hasGiftedAppAccess ? (
          <Paper
            variant="outlined"
            data-testid="membership.override-upsell"
            sx={{
              maxWidth: 560,
              paddingBlock: { xs: 2.5, md: 3 },
              paddingInline: { xs: 2.5, md: 3 },
              backgroundColor: "background.paper",
              backgroundImage:
                "linear-gradient(135deg, var(--mui-palette-background-paper) 0%, color-mix(in srgb, var(--mui-palette-primary-main) 18%, var(--mui-palette-background-paper)) 55%, color-mix(in srgb, var(--mui-palette-primary-main) 34%, var(--mui-palette-background-paper)) 100%)",
            }}
          >
            <Typography variant="H3" sx={{ color: "text.primary" }}>
              {t("membership.gift.title")}
            </Typography>
            <Typography
              variant="T2"
              sx={{ color: "text.secondary", marginTop: 1.25, maxWidth: 440 }}
            >
              {t("membership.gift.body")}
            </Typography>
            <Button
              component={RouterLink}
              to="/membership"
              variant="contained"
              size="small"
              data-testid="membership.override-upsell-action"
              sx={{ marginTop: 2 }}
            >
              {t("membership.gift.action")}
            </Button>
          </Paper>
        ) : (
          <Paper
            variant="outlined"
            data-testid="membership.status"
            sx={{
              maxWidth: 560,
              paddingBlock: { xs: 2.5, md: 3 },
              paddingInline: { xs: 2.5, md: 3 },
              backgroundColor: "background.paper",
              backgroundImage:
                "linear-gradient(135deg, var(--mui-palette-background-paper) 0%, color-mix(in srgb, var(--mui-palette-primary-main) 18%, var(--mui-palette-background-paper)) 55%, color-mix(in srgb, var(--mui-palette-primary-main) 34%, var(--mui-palette-background-paper)) 100%)",
            }}
          >
            <Typography variant="H3" sx={{ color: "text.primary" }}>
              {t(membershipStatusKey)}
            </Typography>
            {user.membership_status === "active" ? (
              <Typography
                variant="T2"
                sx={{ color: "text.secondary", marginTop: 1.25, maxWidth: 440 }}
              >
                {t("membership.status.activeBenefit")}
              </Typography>
            ) : null}
          </Paper>
        )}

        <Paper variant="outlined" sx={{ overflow: "hidden", maxWidth: 560 }}>
          <List disablePadding>
            {/* The first-run orientation, kept reachable afterwards. It sits with
                "ask the team" rather than in the settings card below because neither
                is a preference — both are ways of getting unstuck. */}
            <ListItemButton
              component={RouterLink}
              to={WELCOME_PATH}
              data-testid="profile.welcome"
              sx={{
                width: "100%",
                gap: 1,
                paddingBlock: 1.75,
                paddingInline: 2.5,
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: "link.main" }}>
                <ExploreOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("profile.welcome.title")}
                secondary={t("profile.welcome.subtitle")}
                slotProps={{
                  primary: { variant: "L1", sx: { color: "text.primary" } },
                  secondary: {
                    variant: "T3",
                    sx: { color: "text.muted", marginTop: 0.25 },
                  },
                }}
              />
              <ChevronRightIcon
                sx={{
                  color: "text.muted",
                  flexShrink: 0,
                  fontSize: 24,
                  width: 24,
                  height: 24,
                  marginLeft: "auto",
                }}
              />
            </ListItemButton>

            {/* What the member kept for later. A place to come back to, not a
                setting, which is why it sits here rather than in the card below. */}
            <ListItemButton
              component={RouterLink}
              to={SAVED_PATH}
              data-testid="profile.saved"
              sx={{
                width: "100%",
                gap: 1,
                paddingBlock: 1.75,
                paddingInline: 2.5,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: "link.main" }}>
                <FavoriteBorderIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("profile.savedItems.title")}
                secondary={t("profile.savedItems.subtitle")}
                slotProps={{
                  primary: { variant: "L1", sx: { color: "text.primary" } },
                  secondary: {
                    variant: "T3",
                    sx: { color: "text.muted", marginTop: 0.25 },
                  },
                }}
              />
              <ChevronRightIcon
                sx={{
                  color: "text.muted",
                  flexShrink: 0,
                  fontSize: 24,
                  width: 24,
                  height: 24,
                  marginLeft: "auto",
                }}
              />
            </ListItemButton>

            <ListItemButton
              component="button"
              type="button"
              onClick={() => setChatOpen(true)}
              data-testid="profile.ask-team"
              sx={{
                width: "100%",
                gap: 1,
                paddingBlock: 1.75,
                paddingInline: 2.5,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: "link.main" }}>
                <QuestionAnswerOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("profile.askTeam.title")}
                secondary={t("profile.askTeam.subtitle")}
                slotProps={{
                  primary: { variant: "L1", sx: { color: "text.primary" } },
                  secondary: {
                    variant: "T3",
                    sx: { color: "text.muted", marginTop: 0.25 },
                  },
                }}
              />
              <ChevronRightIcon
                sx={{
                  color: "text.muted",
                  flexShrink: 0,
                  fontSize: 24,
                  width: 24,
                  height: 24,
                  marginLeft: "auto",
                }}
              />
            </ListItemButton>
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ overflow: "hidden", maxWidth: 560 }}>
          <List disablePadding>
            {SETTINGS_ITEMS.map((item, index) => {
              return (
                <SettingsRow
                  key={item.key}
                  item={item}
                  label={t(
                    hasGiftedAppAccess && item.key === "membership"
                      ? "profile.menu.supplementSubscription"
                      : item.labelKey,
                  )}
                  first={index === 0}
                  onAction={handleSettingsAction}
                  externalUrl={
                    hasGiftedAppAccess && item.key === "membership"
                      ? BRAND_URL
                      : undefined
                  }
                />
              );
            })}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ overflow: "hidden", maxWidth: 560 }}>
          <List disablePadding>
            <SettingsRow
              item={LEGAL_SECTION_ITEM}
              label={t(LEGAL_SECTION_ITEM.labelKey)}
              first
            />
          </List>
        </Paper>

        <Button
          variant="outlined"
          color="inherit"
          onClick={() => void signOut()}
          data-testid="profile.sign-out"
          // Full width, capped to the settings card above it so the two edges line up.
          sx={{ alignSelf: "stretch", maxWidth: 560 }}
        >
          {t("profile.signOut")}
        </Button>
      </Box>

      <DeleteAccountDrawer
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
      />
      {chatOpen ? (
        <TeamChatDialog
          open
          userId={user.id}
          onClose={() => setChatOpen(false)}
        />
      ) : null}
    </Screen>
  );
}
